import { useForm, useStore } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { BookOpen } from "lucide-react";
import { useMemo, useState } from "react";

import {
  AdminListPage,
  AdminListTable,
  contentColumns,
  DescriptionField,
  ROW_LINK_CLASS,
  TitleSlugFields,
  useAdminListCreate,
} from "~/components/cih/admin-list-page";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  createGuideFn,
  guideQueryKeys,
  guidesQueryOptions,
  type GuideListRow,
} from "~/lib/functions/cih/guides";
import {
  listSearchSchema,
  listSortDefaults,
  type SortColumn,
  sortListRows,
} from "~/lib/shared/admin-list-sort";
import {
  GUIDE_TYPE_OPTIONS,
  type GuideType,
} from "~/lib/shared/content-status";
import { nextSortSearch } from "~/lib/shared/sort";

const GUIDE_SORTS = ["title", "created"] as const;
type GuideSort = (typeof GUIDE_SORTS)[number];

const GUIDE_SORT_DEFAULTS = listSortDefaults<GuideSort>("title", ["created"]);

const GUIDE_SORT_COLUMNS: Record<GuideSort, SortColumn<GuideListRow>> = {
  title: { kind: "text", value: (row) => row.title },
  created: { kind: "date", value: (row) => row.createdAt },
};

export const Route = createFileRoute("/admin/cih/guides/")({
  staticData: { title: "Guides" },
  validateSearch: listSearchSchema(GUIDE_SORTS),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(guidesQueryOptions());
    return {};
  },
  component: GuidesPage,
});

const GUIDE_COLUMNS: ColumnDef<GuideListRow>[] = [
  {
    id: "title",
    header: "Title",
    cell: ({ row }) => (
      <Link
        to="/admin/cih/guides/$guideId"
        params={{ guideId: row.original.id }}
        className={ROW_LINK_CLASS}
      >
        {row.original.title}
      </Link>
    ),
  },
  {
    id: "type",
    header: "Type",
    cell: ({ row }) => (
      <span className="text-gray-500 capitalize">
        {row.original.guideType.replace(/_/g, " ")}
      </span>
    ),
  },
  {
    id: "category",
    header: "Category",
    cell: ({ row }) => (
      <span className="text-gray-500">{row.original.category ?? "—"}</span>
    ),
  },
  ...contentColumns<GuideListRow>({
    publicPath: (slug) => `/coaching-business/${slug}`,
    pendingDraft: (row, className) =>
      row.pendingDraftId && (
        <Link
          to="/admin/cih/guides/$guideId"
          params={{ guideId: row.pendingDraftId }}
          className={className}
        >
          Draft pending
        </Link>
      ),
  }),
];

function GuidesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate({ from: Route.fullPath });
  const urlSearch = Route.useSearch();
  const sort = urlSearch.sort ?? GUIDE_SORT_DEFAULTS.sort;
  const direction = urlSearch.dir ?? GUIDE_SORT_DEFAULTS.direction(sort);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const create = useAdminListCreate(() => form.reset());

  const { data: guides, isLoading } = useQuery(
    guidesQueryOptions(statusFilter || undefined),
  );

  const createMutation = useMutation({
    mutationFn: createGuideFn,
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: guideQueryKeys.all });
      create.finish("Guide created.");
      navigate({
        to: "/admin/cih/guides/$guideId",
        params: { guideId: created.id },
      });
    },
  });

  const form = useForm({
    defaultValues: {
      title: "",
      slug: "",
      guideType: "stage_guide" as GuideType,
      description: "",
    },
    onSubmit: ({ value }) => {
      createMutation.mutate({
        data: {
          title: value.title.trim(),
          slug: value.slug.trim() || undefined,
          guideType: value.guideType,
          description: value.description.trim(),
        },
      });
    },
  });

  const canCreate = useStore(form.store, ({ values }) =>
    Boolean(values.title.trim() && values.description.trim()),
  );

  const shownGuides = useMemo((): GuideListRow[] => {
    if (!guides) return [];
    const q = search.toLowerCase();
    const matches = q
      ? guides.filter(
          (g) =>
            g.title.toLowerCase().includes(q) ||
            (g.slug ?? "").toLowerCase().includes(q),
        )
      : guides;
    return sortListRows(
      matches,
      sort,
      direction,
      GUIDE_SORT_COLUMNS,
      (g) => g.title,
    );
  }, [guides, search, sort, direction]);

  const changeSort = (column: GuideSort): void => {
    navigate({
      search: (previous) => ({
        ...previous,
        ...nextSortSearch({ sort, direction }, column, GUIDE_SORT_DEFAULTS),
      }),
    });
  };

  const createFields = (
    <>
      <form.Field name="title">
        {(titleField) => (
          <form.Field name="slug">
            {(slugField) => (
              <TitleSlugFields
                create={create}
                idPrefix="guide"
                titleLabel="Title"
                title={titleField.state.value}
                slug={slugField.state.value}
                titlePlaceholder="e.g., How to price your coaching"
                slugPlaceholder="e.g., how-to-price-coaching"
                onTitleChange={titleField.handleChange}
                onSlugChange={slugField.handleChange}
              />
            )}
          </form.Field>
        )}
      </form.Field>
      <form.Field name="guideType">
        {(field) => (
          <div>
            <Label htmlFor="guide-type" className="mb-1">
              Guide type
            </Label>
            <Select
              value={field.state.value}
              onValueChange={(val) => field.handleChange(val as GuideType)}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GUIDE_TYPE_OPTIONS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </form.Field>
      <form.Field name="description">
        {(field) => (
          <DescriptionField
            id="guide-description"
            value={field.state.value}
            placeholder="Brief description of this guide..."
            onChange={field.handleChange}
          />
        )}
      </form.Field>
    </>
  );

  return (
    <AdminListPage
      title="Guides"
      description="Coach-facing how-to content for the Coach Business Builder"
      itemName="Guide"
      create={create}
      createFields={createFields}
      onCreateSubmit={() => form.handleSubmit()}
      canCreate={canCreate}
      isCreating={createMutation.isPending}
      createError={createMutation.error}
      statusFilter={{ value: statusFilter, onChange: setStatusFilter }}
      search={{
        value: search,
        onChange: setSearch,
        placeholder: "Search guides...",
      }}
      isLoading={isLoading}
    >
      <AdminListTable
        rows={shownGuides}
        columns={GUIDE_COLUMNS}
        sorts={GUIDE_SORTS}
        sort={sort}
        direction={direction}
        onSortChange={changeSort}
        empty={{
          isFiltered: Boolean(search),
          icon: BookOpen,
          itemName: "Guide",
          title: "No guides yet",
          description: "Create a coach-facing guide to get started.",
          onCreate: create.open,
        }}
      />
    </AdminListPage>
  );
}
